import React, { useState } from "react";
import { DateDisplay } from "../DateDisplay";
import {
	OperationalConfiguration,
	OperationalConfigurationEditRequest,
	OperationalConfigurationEditRequestInitialValue,
	OperationalConfigurationEditRequestValidationSchema,
} from "../../models/OperationalConfiguration";
import { ValueDisplayProps } from "../ValueDisplay";
import { Formik } from "formik";
import { useSnackbar } from "notistack";
import { FormikTextInputField } from "../Form/FormikTextInput";
import { showSnack } from "../../utils/component";
import { InlineValueDisplayList } from "../InlineValueDisplayList";
import {
	deleteOperationalConfiguration,
	addOperationalConfiguration,
	editOperationalConfiguration,
} from "../../services/api/operationalConfiguration.api";
import { Button } from "@material-ui/core";
import { useHistory } from "react-router-dom";
import { tryGetProp } from "../../utils";

interface OperationalConfigurationEditProps {
	// optional operational configuration object
	// if not provided, form will be displayed in add mode
	operationalConfiguration?: OperationalConfiguration | null;
	// product id
	productId: string;
	// function to call on successful save
	onSuccess?: () => void;
}

const getAuditDataDisplay = (data: OperationalConfiguration) => {
	const detailsToDisplay: ValueDisplayProps[] = [
		{
			label: "Created on",
			node: <DateDisplay date={data.createdDate} />,
		},
		{
			label: "Created by",
			node: data.createdBy,
		},
		{
			label: "Updated on",
			node: <DateDisplay date={data.lastUpdatedDate} />,
		},
		{
			label: "Updated by",
			node: data.updatedBy,
		},
	];

	return <InlineValueDisplayList displayItems={detailsToDisplay}></InlineValueDisplayList>;
};

/**
 * Display an operational configuration
 * @param props
 */
export const OperationalConfigurationEdit = (props: OperationalConfigurationEditProps) => {
	const history = useHistory();
	const { enqueueSnackbar } = useSnackbar();
	const [isFormSubmitting, setIsFormSubmitting] = useState(false);

	const isEditMode = props.operationalConfiguration != null;

	const initialValues: OperationalConfigurationEditRequest = {
		operationalConfigurationId: tryGetProp(
			OperationalConfigurationEditRequestInitialValue.operationalConfigurationId!,
			() => props.operationalConfiguration!.operationalConfigurationId,
		),
		name: tryGetProp(OperationalConfigurationEditRequestInitialValue.name, () => props.operationalConfiguration!.name),
		displayName: tryGetProp(
			OperationalConfigurationEditRequestInitialValue.displayName,
			() => props.operationalConfiguration!.displayName,
		),
		defaultValue: tryGetProp(
			OperationalConfigurationEditRequestInitialValue.defaultValue,
			() => props.operationalConfiguration!.defaultValue,
		),
		description: tryGetProp(
			OperationalConfigurationEditRequestInitialValue.description,
			() => props.operationalConfiguration!.description,
		),
	};

	const handleDelete = () => {
		deleteOperationalConfiguration(props.productId, props.operationalConfiguration!.operationalConfigurationId)()
			.then(() => {
				history.push(`/Products/${props.productId}/OperationalConfigurations`);
			})
			.catch((error) => {
				console.log({ error });
				showSnack(enqueueSnackbar, "There was an error while deleting the operational configuration", "error");
			});
	};

	const submitForm = async (values, { resetForm }) => {
		try {
			setIsFormSubmitting(true);

			if (isEditMode) {
				await editOperationalConfiguration(
					props.productId,
					props.operationalConfiguration!.operationalConfigurationId,
					values,
				)();
			} else {
				await addOperationalConfiguration(props.productId, values)();
				resetForm();
			}

			showSnack(enqueueSnackbar, "Changes saved successfully");

			if (props.onSuccess) {
				props.onSuccess();
			}
		} catch (error) {
			console.log({ error });
			showSnack(enqueueSnackbar, "There was an error while saving changes", "error");
		}

		setIsFormSubmitting(false);
	};

	return (
		<div>
			{isEditMode && getAuditDataDisplay(props.operationalConfiguration!)}

			<Formik
				initialValues={initialValues}
				validationSchema={OperationalConfigurationEditRequestValidationSchema}
				onSubmit={submitForm}
			>
				{({ handleSubmit }) => {
					return (
						<div style={{ marginTop: 30 }}>
							<FormikTextInputField name="name" label="Name" optimized={true} />

							<FormikTextInputField name="displayName" label="Display name" optimized={true} />

							<FormikTextInputField name="defaultValue" label="Default value" optimized={true} />

							<FormikTextInputField name="description" label="description" isTextArea={true} optimized={true} />

							<div style={{ marginTop: 20 }}>
								<Button
									data-testid="edit-operational-configuration-save-btn"
									variant="contained"
									disabled={isFormSubmitting}
									color="primary"
									style={{ marginRight: 12, marginTop: 10 }}
									onClick={() => handleSubmit()}
								>
									{isEditMode ? "Save operational configuration" : "Add operational configuration"}
								</Button>
								{isEditMode && (
									<Button
										data-testid="edit-operational-configuration-delete-btn"
										variant="outlined"
										disabled={isFormSubmitting}
										color="secondary"
										style={{ marginTop: 10 }}
										onClick={() => {
											if (confirm("Are you sure you want to delete this operational configuration?")) {
												handleDelete();
											}
										}}
									>
										Delete operational configuration
									</Button>
								)}
							</div>
						</div>
					);
				}}
			</Formik>
		</div>
	);
};
